import React from 'react';
import { Button } from 'reactstrap';
import SkeletonLoader from './SkeletonLoader';
import './DashboardWidget.css';
import { Link } from 'react-router-dom';

interface DashboardWidgetProps {
  title: string;
  subtitle?: string;
  loading?: boolean;
  error?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRefresh?: () => void;
  seeMoreHref?: string;
  seeMoreLabel?: string;
  className?: string;
  gridColumn?: string;
  children: React.ReactNode;
}

const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  title,
  subtitle,
  loading = false,
  error = false,
  isEmpty = false,
  emptyMessage = 'No data for this period.',
  onRefresh,
  seeMoreHref,
  seeMoreLabel = 'See more',
  gridColumn = '',
  className = '',
  children,
}) => (
  <div className={`dashboard-widget ${className}`} style={{ gridColumn: gridColumn }}>
    <div className="dashboard-widget-header">
      <div>
        <h3 className="dashboard-widget-title">{title}</h3>
        {subtitle && <p className="dashboard-widget-subtitle">{subtitle}</p>}
      </div>
      <div className="dashboard-widget-actions">
        {onRefresh && (
          <Button
            color="light"
            size="sm"
            className="dashboard-widget-refresh"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
          >
            <i className={`bx bx-revision ${loading ? 'bx-spin' : ''}`} />
          </Button>
        )}
        {seeMoreHref && !loading && (
          <Link to={seeMoreHref} className="dashboard-widget-see-more">
            {seeMoreLabel}
            <i className="bx bx-right-arrow-alt" />
          </Link>
        )}
      </div>
    </div>

    {loading && (
      <div className="dashboard-widget-body">
        <SkeletonLoader type="chart" height="220px" />
      </div>
    )}

    {!loading && error && (
      <div className="dashboard-widget-body dashboard-widget-error">
        <p>Could not load this section.</p>
        {onRefresh && (
          <Button color="primary" size="sm" outline onClick={onRefresh}>
            Try again
          </Button>
        )}
      </div>
    )}

    {!loading && !error && isEmpty && (
      <div className="dashboard-widget-body dashboard-widget-empty">
        <p>{emptyMessage}</p>
      </div>
    )}

    {!loading && !error && !isEmpty && <div className="dashboard-widget-body">{children}</div>}
  </div>
);

export default DashboardWidget;
